import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import Curtains from './components/Curtains';
import Interface from './components/Interface';
import SceneErrorBoundary from './components/SceneErrorBoundary';
import './App.css';

const MainScene = lazy(() => import('./components/MainScene'));

type LoadState = 'curtain-loading' | 'curtain-ready' | 'scene-loading' | 'scene-ready' | 'curtains-opening' | 'experience-ready' | 'error';

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

function supportsWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

export default function UnderConstruction() {
  const [state, setState] = useState<LoadState>('curtain-loading');
  const [webglAvailable] = useState(supportsWebGL);
  const reducedMotion = useReducedMotion();
  const isSceneMounted = state === 'scene-loading' || state === 'scene-ready' || state === 'curtains-opening' || state === 'experience-ready';
  const curtainsOpening = state === 'curtains-opening' || state === 'experience-ready';
  const interfaceVisible = state === 'experience-ready';

  const handleCurtainsVisible = useCallback(() => {
    setState((current) => current === 'curtain-loading' ? 'curtain-ready' : current);
  }, []);

  useEffect(() => {
    if (state !== 'curtain-ready') return undefined;
    const frame = window.requestAnimationFrame(() => setState('scene-loading'));
    return () => window.cancelAnimationFrame(frame);
  }, [state]);

  const handleSceneReady = useCallback(() => {
    setState((current) => current === 'scene-loading' ? 'scene-ready' : current);
  }, []);

  useEffect(() => {
    if (state !== 'scene-ready') return undefined;
    // This short pause happens only after every Suspense asset has rendered once.
    const timer = window.setTimeout(() => setState('curtains-opening'), reducedMotion ? 500 : 2000);
    return () => window.clearTimeout(timer);
  }, [state, reducedMotion]);

  useEffect(() => {
    if (state !== 'curtains-opening') return undefined;
    const timer = window.setTimeout(() => setState('experience-ready'), reducedMotion ? 760 : 2550);
    return () => window.clearTimeout(timer);
  }, [state, reducedMotion]);

  // Never leave visitors looking at closed curtains if a remote/browser failure occurs.
  useEffect(() => {
    if (state === 'experience-ready' || state === 'error') return undefined;
    const timeout = window.setTimeout(() => setState('error'), 12000);
    return () => window.clearTimeout(timeout);
  }, [state]);

  const canvasOptions = useMemo(() => ({ powerPreference: 'high-performance' as WebGLPowerPreference, antialias: true, alpha: false }), []);

  if (!webglAvailable || state === 'error') {
    return <main className="construction-page static-fallback"><Interface visible fallback /></main>;
  }

  return (
    <main className="construction-page" data-load-state={state}>
      <Canvas
        className="stage-canvas"
        dpr={[1, window.innerWidth < 720 ? 1.35 : 1.7]}
        gl={canvasOptions}
        shadows={{ type: THREE.PCFSoftShadowMap }}
        camera={{ position: [0, 0.2, 9.2], fov: 44, near: 0.1, far: 45 }}
      >
        <Curtains opening={curtainsOpening} reducedMotion={reducedMotion} onVisible={handleCurtainsVisible} />
        {isSceneMounted && (
          <SceneErrorBoundary onError={() => setState('error')}>
            <Suspense fallback={null}>
              <MainScene onReady={handleSceneReady} reducedMotion={reducedMotion} />
            </Suspense>
          </SceneErrorBoundary>
        )}
      </Canvas>
      <Interface visible={interfaceVisible} />
    </main>
  );
}
